import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, ChevronLeft } from "lucide-react";
import { useAuth } from "../lib/auth";
import { CountryPicker, CityPicker, detectCountryCode } from "../components/LocationPickers";

const Signup = () => {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", mobile: "",
    country: "", city: "", gender: "", dob: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    detectCountryCode().then((code) => {
      setForm((f) => (f.country ? f : { ...f, country: code }));
    });
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setField = (k) => (v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.country) { setErr("Please select your country."); return; }
    if (!form.city.trim()) { setErr("Please enter your city."); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (!payload[k]) delete payload[k]; });
      await register(payload);
      nav("/dashboard");
    } catch (er) {
      setErr(er?.response?.data?.detail || "Signup failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[100dvh] bg-[#FFF7EE] px-6 pt-6 pb-8">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/login" data-testid="signup-back" className="w-9 h-9 rounded-full bg-white flex items-center justify-center border border-[#0A4C36]/10">
          <ChevronLeft size={20} className="text-[#0A4C36]" />
        </Link>
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F4844B] to-[#EF4444] flex items-center justify-center">
          <Heart size={18} className="text-white" fill="white" />
        </div>
      </div>
      <h1 className="font-instrument text-3xl font-semibold text-[#0A4C36] leading-tight">Create account</h1>
      <p className="text-[#57534E] mt-1 mb-6">Join Health Circles and get personalised care.</p>

      <form onSubmit={submit} className="flex flex-col gap-3">
        <Field label="Full name" required>
          <input data-testid="signup-name" required placeholder="e.g. Priya Sharma" className="input-field" value={form.name} onChange={upd("name")} />
        </Field>
        <Field label="Email" required>
          <input data-testid="signup-email" required type="email" placeholder="you@example.com" className="input-field" value={form.email} onChange={upd("email")} />
        </Field>
        <Field label="Password" required hint="At least 6 characters">
          <input data-testid="signup-password" required type="password" minLength={6} placeholder="••••••••" className="input-field" value={form.password} onChange={upd("password")} />
        </Field>
        <Field label="Mobile number" hint="Optional">
          <input data-testid="signup-mobile" placeholder="+91 90000 00000" className="input-field" value={form.mobile} onChange={upd("mobile")} />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Gender" hint="Optional">
            <select data-testid="signup-gender" className="input-field" value={form.gender} onChange={upd("gender")}>
              <option value="">Select</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </Field>
          <Field label="Date of birth" hint="Optional">
            <input data-testid="signup-dob" type="date" className="input-field" value={form.dob} onChange={upd("dob")} />
          </Field>
        </div>

        <Field label="Country" required>
          <CountryPicker
            testid="signup-country"
            required
            value={form.country}
            onChange={(code) => setForm({ ...form, country: code, city: "" })}
          />
        </Field>
        <Field label="City" required>
          <CityPicker
            testid="signup-city"
            required
            countryCode={form.country}
            value={form.city}
            onChange={setField("city")}
          />
        </Field>

        {err && <div data-testid="signup-error" className="text-sm text-red-500 bg-red-50 rounded-xl px-4 py-2.5">{err}</div>}

        <button data-testid="signup-submit" type="submit" disabled={busy} className="btn-primary mt-2">
          {busy ? "Creating…" : "Create account"}
        </button>
      </form>

      <p className="text-center text-sm text-[#57534E] mt-6">
        Already have an account?{" "}
        <Link to="/login" data-testid="signup-to-login" className="text-[#0A4C36] font-semibold">Sign in</Link>
      </p>
    </div>
  );
};

const Field = ({ label, hint, required, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-[#57534E] ml-1 flex items-center gap-1">
      {label}
      {required && <span className="text-red-500">*</span>}
      {hint && !required && <span className="text-[#A8A29E] font-normal">· {hint}</span>}
    </span>
    <div className="mt-1">{children}</div>
  </label>
);

export default Signup;
